import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, usePropertyAddressUrl, usePropertyField, useCollectionBinding } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import pagesData from '../vibesites-pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useColor4 } from '../hooks/useColor4';
import { localizeSpelling, usePropertyCountry, type PropertyCountry } from '../utils/propertyCountry';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================
// Navigation is built dynamically from pages.json, respecting each page's
// "visible" flag and "position" ordering. This ensures that toggling a page's
// visibility in the CMS correctly shows/hides it in navigation.
// Pages like Home, Privacy Policy, Sitemap, etc. are excluded since they are
// not standard navigation items.
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
}

/** Page aliases that should never appear in the main navigation */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
  'lifestyle',
]);

/**
 * Nested nav: child page alias → parent page alias.
 * Kept here (not pages.json) so the schema linter stays clean.
 */
const NAV_PARENT_BY_ALIAS: Record<string, string> = {
  'interactive-map': 'floor-plans',
  'virtual-tour': 'gallery',
  'pet-policy': 'amenities',
  'meet-the-team': 'contact',
  faq: 'contact',
};

interface NavLink {
  label: string;
  href: string;
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Nested via NAV_PARENT_BY_ALIAS when the parent is also in nav.
 * Sorted by position.
 * Pass `hasVirtualTours: false` to suppress the /virtual-tour link.
 */
function buildNavLinks(hasVirtualTours: boolean, country: PropertyCountry): NavLink[] {
  const entries = Object.entries(pagesData as Record<string, PageEntry>).filter(
    ([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false; // Exclude template pages (e.g. hyperlocal)
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      return true;
    },
  );

  const byAlias = new Map(entries);
  const childrenByParent = new Map<string, Array<[string, PageEntry]>>();

  for (const entry of entries) {
    const [alias] = entry;
    const parentAlias = NAV_PARENT_BY_ALIAS[alias];
    if (parentAlias && byAlias.has(parentAlias)) {
      const list = childrenByParent.get(parentAlias) ?? [];
      list.push(entry);
      childrenByParent.set(parentAlias, list);
    }
  }

  return entries
    .filter(([alias]) => {
      const parentAlias = NAV_PARENT_BY_ALIAS[alias];
      return !parentAlias || !byAlias.has(parentAlias);
    })
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([alias, page]) => {
      const kids = childrenByParent.get(alias);
      return {
        label: localizeSpelling(page.name, country),
        href: page.path,
        children: kids
          ?.sort(([, a], [, b]) => a.position - b.position)
          .map(([, child]) => ({
            label: localizeSpelling(child.name, country),
            href: child.path,
          })),
      };
    });
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const addressGroupDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    url: {
      label: 'Map URL',
      type: 'url',
      // URL has no defaultField — defaults to usePropertyAddressUrl() at render
      // time. Editor overrides still win.
      defaultValue: '',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link / Dropdown
// ============================================================================

const navLinkClassName =
  'font-body text-[13px] font-semibold tracking-wider uppercase text-black hover:text-neutral-600 transition-colors py-2 whitespace-nowrap';

const navDropdownItemClassName =
  'block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-black hover:text-black hover:bg-neutral-100 transition-colors';

function DesktopNavLink({ link }: { link: NavLink }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hasChildren = Boolean(link.children?.length);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (!hasChildren) {
    return (
      <a href={link.href} className={navLinkClassName}>
        {link.label}
      </a>
    );
  }

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <a
        href={link.href}
        className={`${navLinkClassName} inline-flex items-center gap-1`}
        aria-expanded={open}
        aria-haspopup="true"
        onFocus={() => setOpen(true)}
      >
        {link.label}
        <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </a>
      <div
        className={`absolute left-0 top-full mt-1 bg-white border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1'
        }`}
      >
        {link.children!.map((child) => (
          <a key={child.href} href={child.href} className={navDropdownItemClassName}>
            {child.label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

const navCollapseEase = 'ease-[cubic-bezier(0.4,0,0.2,1)]';

/** Animates height via grid rows so content of any height expands smoothly. */
function NavCollapse({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      className={`grid transition-[grid-template-rows,opacity,visibility] duration-300 ${navCollapseEase} ${
        open ? 'grid-rows-[1fr] opacity-100 visible' : 'grid-rows-[0fr] opacity-0 invisible'
      }`}
    >
      <div className="overflow-hidden min-h-0">{children}</div>
    </div>
  );
}

function OverflowNavItem({ link }: { link: NavLink }) {
  const [open, setOpen] = useState(false);
  const hasChildren = Boolean(link.children?.length);

  if (!hasChildren) {
    return (
      <a href={link.href} className={navDropdownItemClassName}>
        {link.label}
      </a>
    );
  }

  return (
    <div onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <div className="flex items-stretch">
        <a href={link.href} className={`${navDropdownItemClassName} flex-1`} onFocus={() => setOpen(true)}>
          {link.label}
        </a>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="px-4 text-black hover:bg-neutral-100 transition-colors"
          aria-expanded={open}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${link.label}`}
        >
          <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-300 ${navCollapseEase} ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      <NavCollapse open={open}>
        {link.children!.map((child) => (
          <a
            key={child.href}
            href={child.href}
            className={`${navDropdownItemClassName} pl-8`}
          >
            {child.label}
          </a>
        ))}
      </NavCollapse>
    </div>
  );
}

function OverflowNavLinks({ links }: { links: NavLink[] }) {
  return (
    <>
      {links.map((link) => (
        <OverflowNavItem key={link.href} link={link} />
      ))}
    </>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================
// Measures available space and groups overflow items into a "More" dropdown.
// Uses a hidden measurement row to determine each link's natural width,
// then calculates how many links fit before the CTA button.
// ============================================================================

function DesktopNav({ navLinks }: { navLinks: NavLink[] }) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // Measure how many links fit in the available space
  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    // Read computed gap from the nav container to stay in sync with Tailwind responsive gap
    const computedGap = parseFloat(getComputedStyle(container).gap) || 20;
    const moreButtonWidth = 90; // approximate width of "More ▾" button

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      // If this isn't the last item, reserve space for the "More" button
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        // Check: can ALL remaining items fit without the "More" button?
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;

      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();

    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <>
      {/* Hidden measurement row — renders all links to measure natural widths */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="flex items-center gap-5 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link) => (
          <span
            key={link.href}
            className="font-body text-[13px] font-semibold tracking-wider uppercase inline-flex items-center gap-1"
          >
            {link.label}
            {link.children?.length ? <ChevronDownIcon className="w-3.5 h-3.5" /> : null}
          </span>
        ))}
      </div>

      {/* Visible navigation */}
      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-end gap-5 xl:gap-7 flex-1 min-w-0 mx-6"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link) => (
          <DesktopNavLink key={link.href} link={link} />
        ))}

        {overflowLinks.length > 0 && (
          <div ref={moreRef} className="relative">
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className="font-body text-[13px] font-semibold tracking-wider uppercase text-black hover:text-neutral-600 transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            <div
              className={`absolute right-0 top-full mt-1 bg-white border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-1'
              }`}
            >
              <OverflowNavLinks links={overflowLinks} />
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Mobile Nav Link Component
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
}: {
  link: NavLink;
  onNavigate: () => void;
}) {
  const [open, setOpen] = useState(false);
  const hasChildren = Boolean(link.children?.length);
  const linkClassName =
    'block py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-black hover:text-neutral-600 transition-colors';

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        onClick={onNavigate}
        className={`${linkClassName} border-b border-[var(--vibe-color-border)]/50`}
      >
        {link.label}
      </a>
    );
  }

  return (
    <div
      onPointerEnter={(e) => e.pointerType === 'mouse' && setOpen(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setOpen(false)}
    >
      <div className="flex items-stretch border-b border-[var(--vibe-color-border)]/50">
        <a href={link.href} onClick={onNavigate} className={`${linkClassName} flex-1`}>
          {link.label}
        </a>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="px-6 text-black hover:text-neutral-600 transition-colors"
          aria-expanded={open}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${link.label}`}
        >
          <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${navCollapseEase} ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      <NavCollapse open={open}>
        {link.children!.map((child) => (
          <a
            key={child.href}
            href={child.href}
            onClick={onNavigate}
            className="block py-3 px-10 font-body text-sm font-semibold uppercase tracking-wider text-black/80 hover:text-neutral-600 transition-colors border-b border-[var(--vibe-color-border)]/50"
          >
            {child.label}
          </a>
        ))}
      </NavCollapse>
    </div>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  // True while the user is actively dragging the window edge — we suppress
  // CSS transitions so the header snaps to the new banner height instantly.
  const [isResizing, setIsResizing] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Check whether the property has any virtual tours so we can hide the nav link
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Build navigation links dynamically from pages.json
  const country = usePropertyCountry();
  const navLinks = useMemo(() => buildNavLinks(hasVirtualTours, country), [hasVirtualTours, country]);

  const phone = useContentToken('header.phone');
  const color4 = useColor4();
  const utilityBarStyle = {
    '--header-utility-bg': '#363A35',
    '--header-utility-text': '#ffffff',
  } as CSSProperties;
  const address = useSlotGroup('header.address', addressGroupDef);

  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');

  // If the resolved logo matches the mono-white variant, swap to mono-black
  // so it's visible on the white nav background.
  const logoImage = (logoRaw.image && monoWhiteLogo && logoRaw.image === monoWhiteLogo && monoBlackLogo)
    ? monoBlackLogo
    : logoRaw.image;
  const logo: { image: string | null; alt: string | null } = {
    image: logoImage,
    alt: logoRaw.alt ?? null,
  };

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);

  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();

    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Disable position / size transitions while the window is being resized so
  // the header tracks the (dynamically updated) --offer-banner-height instantly.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      setIsResizing(true);
      clearTimeout(timer);
      timer = setTimeout(() => setIsResizing(false), 200);
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  const propertyAddressUrl = usePropertyAddressUrl();
  // Editor URL → hook fallback → constructed maps directions URL → '#'.
  const directionsUrl = address.url
    || propertyAddressUrl
    || (address.text ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(String(address.text))}` : '#');

  const closeMobileMenu = () => {
    setMenuOpen(false);
  };

  return (
    <>
      {/* Spacer to offset content below the fixed header + offer banner */}
      <div style={{ height: `calc(${headerHeight}px + var(--offer-banner-height, 0px))`, transition: isResizing ? 'none' : 'height 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }} />

      <header ref={headerRef} className="fixed left-0 right-0 z-50 w-full" style={{ top: 'var(--offer-banner-height, 0px)', transition: isResizing ? 'none' : 'top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}>
        {/* ================================================================ */}
        {/* Utility Bar                                                      */}
        {/* ================================================================ */}
        <div className="header-utility-bar" style={utilityBarStyle}>
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-9">
              {/* Left: Address + Phone (desktop) */}
              <div className="hidden md:flex items-center divide-x header-utility-divide">
                <EditableSlotGroup groupPath="header.address" definition={addressGroupDef}>
                  <a
                    href={directionsUrl}
                    target={address._target || '_blank'}
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 pr-4 text-xs tracking-wide hover:opacity-70 transition-opacity"
                  >
                    <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{address.text}</span>
                  </a>
                </EditableSlotGroup>

                <a
                  href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                  className="flex items-center gap-1.5 pl-4 text-xs tracking-wide hover:opacity-70 transition-opacity"
                >
                  <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  <EditableToken
                    path="header.phone"
                    defaultField="property_call_tracking_phone"
                    defaultValue="(555) 555-5555"
                    as="span"
                  >
                    {formatPhone(phone)}
                  </EditableToken>
                </a>
              </div>

              {/* Right: Utility CTA buttons (desktop) */}
              <div className="hidden md:flex items-center divide-x header-utility-divide ml-auto">
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '#'}
                    target={applyNowLink._target || '_blank'}
                    rel={applyNowLink._target === '_self' ? undefined : 'noopener noreferrer'}
                    className="header-utility-cta-btn px-3 py-1 text-[11px] font-semibold tracking-widest uppercase"
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '#'}
                    target={residentAccessLink._target || '_blank'}
                    rel={residentAccessLink._target === '_self' ? undefined : 'noopener noreferrer'}
                    className="header-utility-cta-btn px-3 py-1 text-[11px] font-semibold tracking-widest uppercase"
                  >
                    {residentAccessLink.label || 'Resident Access'}
                  </a>
                </EditableSlotGroup>
              </div>

              {/* Mobile utility row: phone + directions */}
              <div className="md:hidden flex items-center justify-between w-full text-xs">
                <a
                  href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                  className="flex items-center gap-1.5 hover:opacity-70 transition-opacity"
                >
                  <PhoneIcon className="w-3.5 h-3.5" />
                  <span>{formatPhone(phone)}</span>
                </a>
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 hover:opacity-70 transition-opacity"
                >
                  <MapPinIcon className="w-3.5 h-3.5" />
                  <span>Get Directions</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Main Navigation Bar                                              */}
        {/* ================================================================ */}
        <div className="bg-white border-b border-[var(--vibe-color-border)] shadow-sm">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 lg:h-[72px]">
              {/* Logo */}
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a href="/" className="flex-shrink-0">
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logo.alt || 'Community Logo'}
                      sizes="160px"
                      className="h-8 lg:h-12 w-auto max-w-[140px] lg:max-w-[200px] object-contain"
                    />
                  ) : (
                    <div className="font-heading text-lg lg:text-2xl font-bold text-black">
                      The Avenue
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop Navigation Links — built dynamically from pages.json */}
              {/* Uses dynamic overflow detection to show "More" dropdown when needed */}
              <DesktopNav navLinks={navLinks} />

              {/* Right group: CTA Button + Mobile Hamburger — grouped so CTA hugs the hamburger */}
              <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    style={color4?.primaryOverride}
                    className="nav-cta-btn group/cta relative hidden lg:inline-flex items-center gap-2 px-4 py-2.5 lg:px-7 lg:py-3.5 text-[10px] lg:text-[11px] font-bold tracking-[0.15em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
                  >
                    <span className="relative z-10">
                      {scheduleTourLink.label || 'Schedule a Tour'}
                    </span>
                    <svg className="relative z-10 w-3 h-3 lg:w-3.5 lg:h-3.5 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </a>
                </EditableSlotGroup>

                {/* Mobile Hamburger */}
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="lg:hidden relative w-10 h-10 flex items-center justify-center"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span
                    className={`absolute block w-6 h-0.5 bg-black transition-all duration-300 ${
                      menuOpen ? 'rotate-45' : '-translate-y-2'
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-black transition-all duration-300 ${
                      menuOpen ? 'opacity-0' : ''
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-black transition-all duration-300 ${
                      menuOpen ? '-rotate-45' : 'translate-y-2'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Mobile Menu Overlay                                              */}
        {/* ================================================================ */}
        <div
          className={`lg:hidden fixed inset-x-0 bottom-0 bg-white overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{ top: `calc(${headerHeight}px + var(--offer-banner-height, 0px))` }}
        >
          {/* Navigation Items — built dynamically from pages.json */}
          <nav role="navigation" aria-label="Mobile navigation">
            {navLinks.map((link) => (
              <MobileNavLink
                key={link.href}
                link={link}
                onNavigate={closeMobileMenu}
              />
            ))}
          </nav>

          {/* Schedule a Tour CTA */}
          <div className="p-5">
            <a
              href={scheduleTourLink.url || '/schedule-a-tour'}
              target={scheduleTourLink._target || '_self'}
              onClick={closeMobileMenu}
              style={color4?.primaryOverride}
              className="nav-cta-btn group/cta relative flex items-center justify-center gap-2 w-full py-3.5 text-sm font-bold tracking-[0.15em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{scheduleTourLink.label || 'Schedule a Tour'}</span>
              <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </div>

          {/* Utility CTA Buttons */}
          <div className="px-5 pb-3">
            <div className="grid grid-cols-2 gap-2">
              <a
                href={applyNowLink.url || '#'}
                target={applyNowLink._target || '_blank'}
                rel={applyNowLink._target === '_self' ? undefined : 'noopener noreferrer'}
                className="text-center py-2.5 text-[11px] font-bold tracking-wider uppercase border border-black text-black hover:bg-[var(--vibe-color-primary)] hover:border-[var(--vibe-color-primary)] hover:text-[var(--vibe-color-text-on-primary)] transition-all"
              >
                {applyNowLink.label || 'Apply Now'}
              </a>
              <a
                href={residentAccessLink.url || '#'}
                target={residentAccessLink._target || '_blank'}
                rel={residentAccessLink._target === '_self' ? undefined : 'noopener noreferrer'}
                className="text-center py-2.5 text-[11px] font-bold tracking-wider uppercase border border-black text-black hover:bg-[var(--vibe-color-primary)] hover:border-[var(--vibe-color-primary)] hover:text-[var(--vibe-color-text-on-primary)] transition-all"
              >
                {residentAccessLink.label || 'Resident Access'}
              </a>
            </div>
          </div>

          {/* Address & Phone */}
          <div className="px-5 pb-6 pt-3 border-t border-[var(--vibe-color-border)]/50">
            <div className="space-y-3">
              <EditableSlotGroup groupPath="header.address" definition={addressGroupDef}>
                <a
                  href={directionsUrl}
                  target={address._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-sm text-black/60 hover:text-black transition-colors"
                >
                  <MapPinIcon className="w-4 h-4 text-black/50 flex-shrink-0" />
                  <span>{address.text}</span>
                </a>
              </EditableSlotGroup>
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="flex items-center gap-2.5 text-sm text-black/60 hover:text-black transition-colors"
              >
                <PhoneIcon className="w-4 h-4 text-black/50 flex-shrink-0" />
                <span>{formatPhone(phone)}</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      <style>{`
        /* Site-wide typography: route Tailwind weight utilities through the
           design tokens so Design Tokens > Font edits apply everywhere. */
        body {
          font-family: var(--vibe-font-body);
          font-weight: var(--vibe-font-weight-medium);
        }
        .font-light { font-weight: var(--vibe-font-weight-light); }
        .font-normal { font-weight: var(--vibe-font-weight-normal); }
        .font-medium { font-weight: var(--vibe-font-weight-medium); }
        .font-semibold { font-weight: var(--vibe-font-weight-semibold); }
        .font-bold { font-weight: var(--vibe-font-weight-bold); }
        /* --header-utility-bg / --header-utility-text are set inline on the bar. */
        .header-utility-bar {
          background: var(--header-utility-bg, linear-gradient(135deg, var(--vibe-color-secondary) 0%, color-mix(in srgb, var(--vibe-color-secondary) 85%, black) 100%));
          color: var(--header-utility-text, var(--vibe-color-text-on-secondary));
        }
        .header-utility-divide > :not(:first-child) {
          border-color: color-mix(in srgb, var(--header-utility-text, #ffffff) 20%, transparent);
        }
        /* Hover uses a tint derived from the bar's text colour so contrast is
           always correct regardless of how dark/light the background is. */
        .header-utility-cta-btn {
          color: var(--header-utility-text, var(--vibe-color-text-on-secondary));
          transition: color 0.2s ease;
        }
        .header-utility-cta-btn:hover {
          color: color-mix(in srgb, var(--header-utility-text, var(--vibe-color-text-on-secondary)) 70%, transparent);
        }
        .nav-cta-btn {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 2px;
          box-shadow:
            0 1px 3px rgba(0,0,0,0.12),
            0 2px 8px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .nav-cta-btn:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.15),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 35%, transparent);
          transform: translateY(-1px);
        }
        .nav-cta-btn:hover::before {
          left: 100%;
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
  },
  slotGroups: {
    'header.address': {
      label: 'Address',
      fields: {
        text: {
          label: 'Address Text',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '',
        },
        url: { label: 'Map URL', type: 'url', defaultValue: '' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_resident_portal_url',
          suggestedFields: ['property_resident_portal_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
